import React, { useState } from 'react';
import { useWorkingMode } from '../../contexts/WorkingModeContext';
import styles from '../../styles/FloatingWorkingMode.module.css';

interface FloatingWorkingModeProps {
  onToggle?: (isActive: boolean) => void;
  position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
  label?: string;
  activeLabel?: string;
  inactiveLabel?: string;
  icon?: React.ReactNode;
  activeIcon?: React.ReactNode;
  disabled?: boolean;
}

const FloatingWorkingMode: React.FC<FloatingWorkingModeProps> = ({
  onToggle,
  position = 'bottom-right',
  label = 'Working Mode',
  activeLabel = 'Active',
  inactiveLabel = 'Inactive',
  icon,
  activeIcon,
  disabled = false,
}) => {
  const { isWorkMode, toggleWorkMode } = useWorkingMode();
  const [isHovered, setIsHovered] = useState(false);

  const handleToggle = () => {
    if (disabled) return;
    
    toggleWorkMode();
    onToggle?.(!isWorkMode);
  };

  const positionClass = styles[position] || styles.bottomRight;

  return (
    <div className={`${styles.floatingButton} ${positionClass} ${isWorkMode ? styles.active : ''} ${disabled ? styles.disabled : ''}`}>
      <button
        className={styles.button}
        onClick={handleToggle}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        disabled={disabled}
        aria-label={isWorkMode ? activeLabel : inactiveLabel}
        title={isWorkMode ? activeLabel : inactiveLabel}
      >
        <div className={styles.iconContainer}>
          {isWorkMode ? (
            activeIcon || (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                fill="currentColor"
                viewBox="0 0 16 16"
                className={styles.icon}
              >
                <path d="M4 16s-1 0-1-1 1-4 5-4 5 3 5 4-1 1-1 1zm4-5.95a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5"/>
                <path d="M2 1a2 2 0 0 0-2 2v9.5A1.5 1.5 0 0 0 1.5 14h.653a5.4 5.4 0 0 1 1.066-2H1V3a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1v9h-2.219c.554.654.89 1.373 1.066 2h.653a1.5 1.5 0 0 0 1.5-1.5V3a2 2 0 0 0-2-2z"/>
              </svg>
            )
          ) : (
            icon || (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                fill="currentColor"
                viewBox="0 0 16 16"
                className={styles.icon}
              >
                <path d="M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6m2-3a2 2 0 1 1-4 0 2 2 0 0 1 4 0m4 8c0 1-1 1-1 1H3s-1 0-1-1 1-4 6-4 6 3 6 4m-1-.004c-.001-.246-.154-.986-.832-1.664C11.516 10.68 10.289 10 8 10c-2.29 0-3.516.68-4.168 1.332-.678.678-.83 1.418-.832 1.664z"/>
              </svg>
            )
          )}
        </div>
        
        {(isHovered || isWorkMode) && (
          <span className={styles.label}>
            {isWorkMode ? activeLabel : inactiveLabel}
          </span>
        )}
        
        <div className={`${styles.ripple} ${isWorkMode ? styles.rippleActive : ''}`}></div>
      </button>
      
      {isWorkMode && (
        <div className={styles.pulseRing}></div>
      )}
    </div>
  );
};

export default FloatingWorkingMode;

